import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

const FriendsDashboard = () => {
  const [friendPairs, setFriendPairs] = useState(0);
  const [unmatchedFriendships, setUnmatchedFriendships] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFriendsData = async () => {
      setLoading(true);
      setError(null);
      try {
        const getFriendsFunc = httpsCallable(functions, 'getFriends');
        const result = await getFriendsFunc();
        const friendships = result.data.friendships || [];

        // Process friendships to find pairs
        const processedSet = new Set();
        let pairCount = 0;
        const unmatchedList = [];

        friendships.forEach((friendship) => {
          const key = `${friendship.userId}-${friendship.friendUserId}`;
          
          // Skip if already processed
          if (processedSet.has(key)) {
            return;
          }

          // Check for the reverse friendship
          const reverseKey = `${friendship.friendUserId}-${friendship.userId}`;
          const hasReverse = friendships.some(
            f => f.userId === friendship.friendUserId && f.friendUserId === friendship.userId
          );

          if (hasReverse) {
            // Mark both as processed
            processedSet.add(key);
            processedSet.add(reverseKey);
            pairCount++;
          } else {
            // Unmatched friendship
            unmatchedList.push(friendship);
            processedSet.add(key);
          }
        });

        setFriendPairs(pairCount);
        setUnmatchedFriendships(unmatchedList);
      } catch (err) {
        console.error("Failed to fetch friends data:", err);
        setError("Failed to load friends data");
      }
      setLoading(false);
    };

    fetchFriendsData();
  }, []);

  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsResult, setSuggestionsResult] = useState(null);
  const [suggestionsError, setSuggestionsError] = useState(null);

  const [selectedPairs, setSelectedPairs] = useState(new Set());
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const handleGetFriendSuggestions = async () => {
    setSuggestionsLoading(true);
    setSuggestionsError(null);
    setSuggestionsResult(null);
    setSelectedPairs(new Set());
    setSaveResult(null);
    setSaveError(null);

    try {
      const getFriendSuggestionsFunc = httpsCallable(functions, 'getFriendSuggestions');
      const result = await getFriendSuggestionsFunc();
      const candidatesMap = result.data?.candidates || {};
      setSuggestionsResult(candidatesMap);

      // Default select all candidate pairs
      const allKeys = new Set();
      Object.entries(candidatesMap).forEach(([userId, candidates]) => {
        candidates.forEach(cId => allKeys.add(`${userId}:${cId}`));
      });
      setSelectedPairs(allKeys);
    } catch (err) {
      console.error("Failed to run getFriendSuggestions:", err);
      setSuggestionsError(err.message || "Failed to fetch friend suggestions");
    } finally {
      setSuggestionsLoading(false);
    }
  };

  // Helper to compute total pairs
  const getAllPairs = () => {
    if (!suggestionsResult) return [];
    const pairs = [];
    Object.entries(suggestionsResult).forEach(([userId, candidates]) => {
      candidates.forEach(cId => {
        pairs.push({ userId, candidateId: cId, key: `${userId}:${cId}` });
      });
    });
    return pairs;
  };

  const allPairs = getAllPairs();
  const isAllSelected = allPairs.length > 0 && selectedPairs.size === allPairs.length;

  const handleToggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedPairs(new Set());
    } else {
      const newSet = new Set(allPairs.map(p => p.key));
      setSelectedPairs(newSet);
    }
  };

  const handleToggleUserSelect = (userId, candidates) => {
    const userKeys = candidates.map(cId => `${userId}:${cId}`);
    const isUserFullySelected = userKeys.length > 0 && userKeys.every(k => selectedPairs.has(k));
    const nextSet = new Set(selectedPairs);

    userKeys.forEach(k => {
      if (isUserFullySelected) {
        nextSet.delete(k);
      } else {
        nextSet.add(k);
      }
    });
    setSelectedPairs(nextSet);
  };

  const handleToggleSinglePair = (pairKey) => {
    const nextSet = new Set(selectedPairs);
    if (nextSet.has(pairKey)) {
      nextSet.delete(pairKey);
    } else {
      nextSet.add(pairKey);
    }
    setSelectedPairs(nextSet);
  };

  const handleSaveFriendSuggestions = async () => {
    if (selectedPairs.size === 0) return;

    setSaveLoading(true);
    setSaveError(null);
    setSaveResult(null);

    try {
      const rawCandidatesToSave = {};
      Object.entries(suggestionsResult).forEach(([userId, candidates]) => {
        const selected = candidates.filter(cId => selectedPairs.has(`${userId}:${cId}`));
        if (selected.length > 0) {
          rawCandidatesToSave[userId] = selected;
        }
      });

      const saveFunc = httpsCallable(functions, 'saveFriendSuggestions');
      const res = await saveFunc({ rawCandidates: rawCandidatesToSave });
      setSaveResult(res.data);
    } catch (err) {
      console.error("Failed to save friend suggestions:", err);
      setSaveError(err.message || "Failed to save friend suggestions");
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <div className="friends-dashboard" style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
      <h1>Friends Dashboard</h1>
      
      {loading && <p>Loading friends statistics...</p>}
      {error && <p style={{ color: 'red' }}>{error}</p>}

      {!loading && (
        <>
          <div style={{ marginBottom: '30px', backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px', border: '1px solid #e9ecef' }}>
            <h2 style={{ marginTop: 0, color: '#2c3e50' }}>Friend Pairs: {friendPairs}</h2>
          </div>

          {unmatchedFriendships.length > 0 && (
            <div style={{ marginTop: '20px', marginBottom: '30px', backgroundColor: '#fff5f5', padding: '20px', borderRadius: '8px', border: '1px solid #feb2b2' }}>
              <h3 style={{ color: '#c53030', marginTop: 0 }}>Unmatched Friendships:</h3>
              <div style={{ color: '#9b2c2c', fontSize: '14px' }}>
                {unmatchedFriendships.map((friendship, index) => (
                  <div key={index}>{friendship.userId} → {friendship.friendUserId}</div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Friend Suggestion Engine */}
      <div style={{ marginTop: '40px', backgroundColor: '#ffffff', padding: '24px', borderRadius: '10px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
        <h2 style={{ marginTop: 0, color: '#1a202c', borderBottom: '2px solid #edf2f7', paddingBottom: '12px' }}>
          Friend Suggestions Location Engine
        </h2>
        <p style={{ color: '#4a5568', fontSize: '14px', marginBottom: '20px' }}>
          Queries active users (last active within 30 days), updates stale geohashes from swim bounding boxes, and generates candidate friend matches based on shared location geohashes.
        </p>

        <button
          onClick={handleGetFriendSuggestions}
          disabled={suggestionsLoading}
          style={{
            backgroundColor: suggestionsLoading ? '#a0aec0' : '#3182ce',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '6px',
            fontSize: '15px',
            fontWeight: '600',
            cursor: suggestionsLoading ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s ease',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
          }}
        >
          {suggestionsLoading ? 'Fetching Location Matches...' : 'Get Friend Suggestions'}
        </button>

        {suggestionsError && (
          <div style={{ marginTop: '20px', padding: '12px', backgroundColor: '#fff5f5', color: '#e53e3e', borderRadius: '6px', border: '1px solid #fed7d7' }}>
            <strong>Error:</strong> {suggestionsError}
          </div>
        )}

        {suggestionsResult && (
          <div style={{ marginTop: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
              <h3 style={{ color: '#2d3748', fontSize: '16px', margin: 0 }}>
                Candidate Match Results ({Object.keys(suggestionsResult).length} Users with Candidates):
              </h3>

              {allPairs.length > 0 && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', color: '#2b6cb0', backgroundColor: '#ebf8ff', padding: '6px 12px', borderRadius: '6px', border: '1px solid #bee3f8' }}>
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleToggleSelectAll}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  Select All ({selectedPairs.size} / {allPairs.length} selected)
                </label>
              )}
            </div>

            {Object.keys(suggestionsResult).length === 0 ? (
              <p style={{ color: '#718096', fontStyle: 'italic' }}>No location-matched candidate pairs found among active users.</p>
            ) : (
              <div style={{ display: 'grid', gap: '12px', marginBottom: '20px' }}>
                {Object.entries(suggestionsResult).map(([userId, candidates]) => {
                  const userKeys = candidates.map(cId => `${userId}:${cId}`);
                  const isUserSelected = userKeys.length > 0 && userKeys.every(k => selectedPairs.has(k));
                  const isUserPartiallySelected = userKeys.some(k => selectedPairs.has(k)) && !isUserSelected;

                  return (
                    <div key={userId} style={{ padding: '14px', backgroundColor: '#f7fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                        <input
                          type="checkbox"
                          checked={isUserSelected}
                          ref={el => { if (el) el.indeterminate = isUserPartiallySelected; }}
                          onChange={() => handleToggleUserSelect(userId, candidates)}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span style={{ fontWeight: 'bold', color: '#2b6cb0', fontSize: '15px' }}>User ID: {userId}</span>
                      </div>

                      <div style={{ fontSize: '14px', color: '#4a5568', paddingLeft: '28px' }}>
                        Suggested Candidates:
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                          {candidates.map(cId => {
                            const pairKey = `${userId}:${cId}`;
                            const isPairSelected = selectedPairs.has(pairKey);
                            return (
                              <label
                                key={cId}
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleToggleSinglePair(pairKey);
                                }}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  backgroundColor: isPairSelected ? '#ebf8ff' : '#edf2f7',
                                  color: isPairSelected ? '#2b6cb0' : '#718096',
                                  border: `1px solid ${isPairSelected ? '#90cdf4' : '#cbd5e0'}`,
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  fontWeight: '500',
                                  cursor: 'pointer',
                                  userSelect: 'none'
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isPairSelected}
                                  onChange={() => {}}
                                  style={{ cursor: 'pointer' }}
                                />
                                {cId}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Save Button & Results Applied Section */}
            {allPairs.length > 0 && (
              <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                <button
                  onClick={handleSaveFriendSuggestions}
                  disabled={saveLoading || selectedPairs.size === 0}
                  style={{
                    backgroundColor: (saveLoading || selectedPairs.size === 0) ? '#cbd5e0' : '#38a169',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '6px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: (saveLoading || selectedPairs.size === 0) ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s ease',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  {saveLoading ? 'Saving Suggestions...' : `Save Friends Suggestions (${selectedPairs.size} Selected)`}
                </button>

                {saveError && (
                  <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#fff5f5', color: '#e53e3e', borderRadius: '6px', border: '1px solid #fed7d7' }}>
                    <strong>Save Error:</strong> {saveError}
                  </div>
                )}

                {saveResult && (
                  <div style={{ marginTop: '16px', padding: '14px', backgroundColor: '#f0fff4', color: '#276749', borderRadius: '6px', border: '1px solid #9ae6b4' }}>
                    <div style={{ fontWeight: 'bold', fontSize: '15px', marginBottom: '4px' }}>Suggestions Applied & Persisted!</div>
                    <div style={{ fontSize: '14px' }}>
                      Status: {saveResult.status} | Written Count: {saveResult.writtenCount} | Batches Committed: {saveResult.batchCount || 1} | Notified Count: {saveResult.notifiedCount}
                    </div>
                  </div>
                )}
              </div>
            )}

            <details style={{ marginTop: '24px' }}>
              <summary style={{ cursor: 'pointer', color: '#4a5568', fontWeight: '500', fontSize: '14px' }}>View Raw JSON Output</summary>
              <pre style={{ backgroundColor: '#1a202c', color: '#63b3ed', padding: '16px', borderRadius: '6px', overflowX: 'auto', fontSize: '13px', marginTop: '8px' }}>
                {JSON.stringify(suggestionsResult, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </div>
    </div>
  );
};

export default FriendsDashboard;
